import {
  Modal,
  Box,
  Typography,
  TextField,
  CircularProgress,
  styled,
  Backdrop,
} from "@mui/material";
import { Formik, Form } from "formik";
import { otpSchema } from "@/schemas/auth.schema";
import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.8)" : "rgba(0, 0, 0, 0.5)",
  backdropFilter: "blur(4px)",
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 25px 50px rgba(0,0,0,0.8)"
      : "0 25px 50px rgba(0,0,0,0.15)",
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  outline: "none",
}));

const OtpModal = ({ open, onClose, onSubmit, isLoading, errorMsg }) => {
  // Only allow manual dismissal via the Cancel button. Ignore backdrop clicks
  // and the Escape key so an accidental click outside can't close the modal
  // (and release the 2FA lock) mid-challenge.
  const handleClose = (_event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    if (isLoading) return;
    onClose?.();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      slots={{ backdrop: StyledBackdrop }}
    >
      <ModalBox>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Two-Factor Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Check your email for the OTP code and enter it below.
          </Typography>
        </Box>

        <Formik
          initialValues={{ otp: "" }}
          validationSchema={otpSchema}
          onSubmit={onSubmit}
        >
          {({ values, handleChange, handleBlur, errors, touched }) => (
            <Form>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="OTP Code"
                  name="otp"
                  value={values.otp}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.otp && errors.otp)}
                  helperText={touched.otp && errors.otp}
                  variant="outlined"
                  size="small"
                  disabled={isLoading}
                  autoFocus
                />

                {errorMsg && <CustomErrorMessage message={errorMsg} />}

                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <CustomButton
                    variantType="outline"
                    onClick={onClose}
                    disabled={isLoading}
                    sx={{ flex: 1, height: 42 }}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    type="submit"
                    disabled={isLoading}
                    sx={{ flex: 1, height: 42 }}
                  >
                    {isLoading ? (
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                    ) : (
                      "Verify OTP"
                    )}
                  </CustomButton>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </ModalBox>
    </Modal>
  );
};

export default OtpModal;
